/**
 * entityImages.service.ts
 *
 * Images attached to catalog records (product types, appointment types,
 * memberships, facilities) through `product_image_urls`, plus the photo library
 * the picker chooses from. Single account only: nothing here passes
 * `all_accounts`, so every call is scoped to the signed-in account.
 *
 * The upload goes through ONE function, `uploadEntityImage`, so the endpoint can
 * be switched in one place.
 */
import { apiClient } from './apiClient';
import { uploadFile } from './uploads.service';

/** 'logo' is an account branding logo: uploaded and listed, never assigned to a record. */
export type EntityImageType = 'product-type' | 'appointment-type' | 'membership' | 'facility' | 'logo' | 'account-alert';

/** A `product_image_urls` row as `GET /product-image-urls` returns it. */
export interface AssignedEntityImage {
  productImageID: number;
  productUrl: string;
  /** 1 = this entity's default image. */
  defaultUrl: number;
  description?: string | null;
  productTypeID?: number;
  appointmentTypeID?: number;
  membershipTemplateID?: number;
  facilityID?: number;
  /** Joined for type=product-type. */
  productTypeName?: string | null;
}

/** A photo-library row as `GET /public-photos` returns it. */
export interface LibraryPhoto {
  id: number;
  url: string;
  originalFilename: string | null;
  altText: string | null;
  folder: string | null;
  folderID?: number | null;
  fileSize?: number;
  shared?: number | boolean;
}

/** A folder row as `GET /public-photos/folders` returns it. */
export interface PhotoFolder {
  id: number | null;
  folder: string;
  count: number;
  scope?: 'master' | 'account';
  isSystem?: boolean;
}

/** The system photo folder each entity's images belong in. */
export const ENTITY_IMAGE_FOLDERS: Record<EntityImageType, string> = {
  'product-type': 'products',
  'appointment-type': 'appointments',
  membership: 'memberships',
  facility: 'facilities',
  logo: 'logos',
  'account-alert': 'alerts',
};

/** Largest image the picker accepts. */
export const MAX_ENTITY_IMAGE_BYTES = 10 * 1024 * 1024;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rows<T>(r: any): T[] {
  const data = r?.data?.data;
  return Array.isArray(data) ? (data as T[]) : [];
}

/** Images already assigned to one entity, newest first. */
export async function listAssignedImages(
  type: EntityImageType,
  id: number,
): Promise<AssignedEntityImage[]> {
  const r = await apiClient.get('/product-image-urls', { params: { type, type_id: id } });
  return rows<AssignedEntityImage>(r);
}

/** The photo library, optionally filtered by folder name and search text. */
export async function listLibraryPhotos(
  params: { folder?: string; search?: string } = {},
): Promise<LibraryPhoto[]> {
  const query: Record<string, string> = {};
  if (params.folder) query.folder = params.folder;
  if (params.search?.trim()) query.search = params.search.trim();
  const r = await apiClient.get('/public-photos', { params: query });
  return rows<LibraryPhoto>(r);
}

/** Photo-library folders, for the folder filter. */
export async function listPhotoFolders(): Promise<PhotoFolder[]> {
  const r = await apiClient.get('/public-photos/folders');
  return rows<PhotoFolder>(r);
}

/**
 * Make `url` the entity's default image. Used by the picker for a saved entity,
 * and by callers after creating one the picker returned a URL for.
 */
export async function assignEntityImage(
  type: EntityImageType,
  id: number,
  url: string,
): Promise<void> {
  await apiClient.post('/product-image-urls', { type, type_id: id, url, default: true });
}

/**
 * Upload an image file for an entity and return its public URL. The API stores
 * it under the account's image prefix and records it in the photo library; it
 * does NOT assign it — call `assignEntityImage` for that.
 *
 * `uploadFile` sends files over 4 MB through the presigned-S3 flow (`s3Key`)
 * instead of multipart, and only reports progress on the multipart path.
 */
export async function uploadEntityImage(
  file: File,
  type: EntityImageType,
  onProgress?: (percent: number) => void,
  folder?: string,
): Promise<string> {
  const fields: Record<string, string> = { type };
  if (folder) fields.folder = folder;
  const r = await uploadFile('/product-image-urls/upload', file, fields, {
    fileFieldName: 'image',
    onProgress,
  });
  const url = r?.data?.data?.url as string | undefined;
  if (!url) throw new Error('The upload finished but no image URL came back.');
  return url;
}

/** The entity's default image URL, else its newest one, else null. */
export async function getDefaultEntityImageUrl(
  type: EntityImageType,
  id: number,
): Promise<string | null> {
  const images = await listAssignedImages(type, id);
  const chosen = images.find((i) => Number(i.defaultUrl) === 1) ?? images[0];
  return chosen?.productUrl?.trim() || null;
}
